import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CloudRainWind,
  Cylinder,
  Droplet,
  Droplets,
  Layers,
  Move,
  Sprout,
} from "lucide-react";
import { useContent } from "@/content/ContentContext";

const ease = [0.22, 1, 0.36, 1];

const iconMap = {
  "hdpe-pipes": Cylinder,
  "pvc-pipes": Layers,
  "sprinkler-systems": CloudRainWind,
  "mini-sprinklers": Droplets,
  "micro-sprinklers": Droplet,
  "portable-sprinklers": Move,
  "drip-systems": Droplet,
  "rain-guns": CloudRainWind,
};

export default function ProductsOffer() {
  const content = useContent();
  const main = content.products.slice(0, 8);

  return (
    <section data-testid="products-offer-section" className="border-t border-line">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease }}
              className="text-xs uppercase tracking-[0.3em] text-harvest"
            >
              What We Offer
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1, ease }}
              data-testid="products-offer-heading"
              className="mt-4 font-display text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl"
            >
              Products <span className="italic text-harvest">We Offer.</span>
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease }}
          >
            <Link
              to="/products"
              data-testid="view-all-products-link"
              className="group inline-flex items-center gap-2 border border-line px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:border-harvest hover:text-harvest"
            >
              View All Products
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <div className="mt-14 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          {main.map((p, i) => {
            const Icon = iconMap[p.id] || Sprout;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease }}
              >
                <Link
                  to={`/products/${p.id}`}
                  data-testid={`offer-${p.id}`}
                  className="group flex h-full flex-col items-center pb-6 text-center transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="relative w-full">
                    <div className="h-44 w-full overflow-hidden rounded-xl sm:h-48">
                      <img
                        src={p.img}
                        alt={p.alt}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <span className="absolute -bottom-7 left-1/2 flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-full border border-line bg-white text-cream shadow-md shadow-[#0B1A33]/10 transition-colors duration-300 group-hover:border-harvest group-hover:text-harvest">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </span>
                  </div>
                  <p className="mt-11 flex min-h-[3.75rem] items-center justify-center font-display text-xl font-bold tracking-tight text-cream transition-colors duration-300 group-hover:text-harvest sm:text-2xl">
                    {p.name}
                  </p>
                  <p className="mt-1.5 text-xs uppercase tracking-[0.15em] text-sage">
                    {p.hindi}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
